import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faMapMarkerAlt, faLocationArrow } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import { NOMINATIM_SEARCH } from '../../utils/constants';

// Use Leaflet from node_modules for stable bundling and offline builds
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// Import marker images so webpack (CRA) can bundle them and Leaflet finds them
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Ensure default icon paths are correct when bundled
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/**
 * Common Map Component
 * Reusable OpenStreetMap component with intelligent location detection
 * - Current location access with permission request
 * - Smart location prioritization: state → city+state → location string in state
 * - Direct map URL opening if provided
 * - No loading UI, instant map display
 */
const Map = ({
  isOpen = false,
  onClose,
  onLocationSelect,
  initialLocation = null, // { country, state, city, delivery_location, map_url, lat, lng }
  title,
  height = '70vh',
  width = '100%',
  defaultCenter = { lat: 20.5937, lng: 78.9629 }, // India center
  defaultZoom = 5,
  selectedZoom = 14,
}) => {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [error, setError] = useState('');

  const strings = t('locations.forms.map', { returnObjects: true });
  const effectiveTitle = title ?? strings.title;

  // Leaflet is imported from node_modules above; no dynamic CDN loading required.

  // Get current location with permission request
  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setError(strings.errors.geolocationNotSupported);
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setError(''); // Clear any previous errors
          resolve({ lat: latitude, lng: longitude });
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              setError(strings.errors.permissionDenied);
              break;
            case error.POSITION_UNAVAILABLE:
              setError(strings.errors.positionUnavailable);
              break;
            case error.TIMEOUT:
              setError(strings.errors.timeout);
              break;
            default:
              setError(strings.errors.unknown);
              break;
          }
          resolve(null);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 10000, 
          maximumAge: 300000 
        }
      );
    });
  }, [strings.errors.geolocationNotSupported, strings.errors.permissionDenied, strings.errors.positionUnavailable, strings.errors.timeout, strings.errors.unknown]);

  // Geocode address using Nominatim with state context
  const geocodeAddress = useCallback(async (query, stateContext = null) => {
    if (!query) return null;

    try {
      // Build search query with state context if provided
      let searchQuery = query;
      if (stateContext && !query.toLowerCase().includes(stateContext.toLowerCase())) {
        searchQuery = `${query}, ${stateContext}`;
      }

  const url = `${NOMINATIM_SEARCH}?q=${encodeURIComponent(searchQuery)}&format=jsonv2&limit=1&countrycodes=in`;
      const response = await fetch(url, { 
        headers: { 
          'Accept': 'application/json',
          'User-Agent': 'ERP-UI-LocationForm/1.0'
        } 
      });
      
      if (!response.ok) return null;
      
      const results = await response.json();
      if (!Array.isArray(results) || results.length === 0) return null;
      
      const result = results[0];
      return {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        display_name: result.display_name,
      };
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }, []);

  // Parse coordinates from map URL
  const parseMapUrl = useCallback((mapUrl) => {
    if (!mapUrl) return null;

    try {
      const osmMatch = mapUrl.match(/[?&#]map=\d+\/(-?\d+\.?\d*?)\/(-?\d+\.?\d*)/);
      if (osmMatch) {
        return { lat: parseFloat(osmMatch[1]), lng: parseFloat(osmMatch[2]) };
      }

      // Alternative OSM format with mlat/mlon
      const mlatMatch = mapUrl.match(/[?&]mlat=(-?\d+\.?\d*).*?[?&]mlon=(-?\d+\.?\d*)/);
      if (mlatMatch) {
        return { lat: parseFloat(mlatMatch[1]), lng: parseFloat(mlatMatch[2]) };
      }

      // Google Maps URL format: https://maps.google.com/?q=lat,lng
      const googleMatch = mapUrl.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (googleMatch) {
        return { lat: parseFloat(googleMatch[1]), lng: parseFloat(googleMatch[2]) };
      }

      // Google Maps @lat,lng format
      const atMatch = mapUrl.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (atMatch) {
        return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };
      }

      return null;
    } catch (error) {
      console.error('Error parsing map URL:', error);
      return null;
    }
  }, []);

  // Determine initial map location based on priority
  const determineInitialLocation = useCallback(async () => {
    const location = initialLocation || {};

    // Priority 1: If map URL is provided, parse and use it
    if (location.map_url) {
      const coords = parseMapUrl(location.map_url);
      if (coords) {
        return { ...coords, zoom: selectedZoom };
      }
    }

    // Priority 2: Explicit coordinates
    if (location.lat && location.lng) {
      return { lat: location.lat, lng: location.lng, zoom: selectedZoom };
    }

    // Priority 3: Location string (delivery_location) — prefer with state context if available
    if (location.delivery_location) {
      // Try the full delivery_location first
      let geocoded = await geocodeAddress(location.delivery_location, location.state || null);
      if (geocoded) {
        return { lat: geocoded.lat, lng: geocoded.lng, zoom: selectedZoom };
      }

      // Fallback: split by commas and try using the last two meaningful segments
      try {
        const parts = String(location.delivery_location).split(',').map(p => p.trim()).filter(Boolean);
        if (parts.length >= 2) {
          const lastTwo = parts.slice(-2).join(', ');
          geocoded = await geocodeAddress(lastTwo, location.state || null);
          if (geocoded) {
            return { lat: geocoded.lat, lng: geocoded.lng, zoom: selectedZoom };
          }
        }
      } catch (err) {
        // ignore and continue to other fallbacks
        console.warn('Error processing delivery_location fallback:', err);
      }
    }

    // Priority 4: City + State
    if (location.city && location.state) {
      const cityState = `${location.city}, ${location.state}`;
      const geocoded = await geocodeAddress(cityState);
      if (geocoded) {
        return { lat: geocoded.lat, lng: geocoded.lng, zoom: selectedZoom };
      }
    }

    // Priority 5: State only
    if (location.state) {
      const geocoded = await geocodeAddress(location.state);
      if (geocoded) {
        return { lat: geocoded.lat, lng: geocoded.lng, zoom: Math.max(defaultZoom + 2, 7) };
      }
    }

    // Priority 6: Default center
    return { lat: defaultCenter.lat, lng: defaultCenter.lng, zoom: defaultZoom };
  }, [initialLocation, parseMapUrl, geocodeAddress, selectedZoom, defaultCenter, defaultZoom]);

  // Initialize map when modal opens
  useEffect(() => {
    let mounted = true;

    if (!isOpen) {
      // Clean up when modal closes
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch (e) {
          console.error('Error cleaning up map:', e);
        }
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
      return;
    }

    const initializeMap = async () => {
      try {
  // Leaflet imported from node_modules (L is available)
  if (!mounted) return;

        // Determine initial location
        const initialPos = await determineInitialLocation();
        if (!mounted) return;

        // Create map instantly (no loading UI)
        const map = L.map(mapRef.current).setView([initialPos.lat, initialPos.lng], initialPos.zoom);
        mapInstanceRef.current = map;

        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: strings.attribution,
          maxZoom: 19,
        }).addTo(map);

        // Create draggable marker
        const marker = L.marker([initialPos.lat, initialPos.lng], { 
          draggable: true,
          title: strings.markerTitle,
        }).addTo(map);

        markerRef.current = marker;

        // Handle marker drag end
        marker.on('dragend', () => {
          // Just wait for user confirmation
        });

        // Handle map click
        map.on('click', (event) => {
          const { lat, lng } = event.latlng;
          marker.setLatLng([lat, lng]);
        });

      } catch (err) {
        console.error('Error initializing map:', err);
        if (mounted) {
          setError(strings.errors.mapLoad);
        }
      }
    };

    initializeMap();

    return () => {
      mounted = false;
    };
  }, [isOpen, determineInitialLocation, strings.attribution, strings.errors.mapLoad, strings.markerTitle]);

  // Handle current location button
  const handleUseCurrentLocation = async () => {
    if (!mapInstanceRef.current || !markerRef.current) return;

    const current = await getCurrentLocation();
    if (current) {
      mapInstanceRef.current.setView([current.lat, current.lng], selectedZoom);
      markerRef.current.setLatLng([current.lat, current.lng]);
    }
  };

  // Handle use selected location - only return coordinates and Google Maps URL
  const handleUseSelectedLocation = () => {
    if (!markerRef.current) return;

    try {
      const position = markerRef.current.getLatLng();
      const lat = position.lat;
      const lng = position.lng;
      
      const locationData = {
        lat,
        lng,
        map_url: `https://maps.google.com/?q=${lat},${lng}`,
      };
      
      onLocationSelect?.(locationData);
      onClose?.();
    } catch (err) {
      setError(strings.errors.selectedLocation);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: UI_COLORS.TEXT.PRIMARY }} />
            <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {effectiveTitle}
            </h3>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Current Location Button */}
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1),
                color: UI_COLORS.STATUS.INFO,
                border: `1px solid ${UI_COLORS.STATUS.INFO}`,
              }}
              title={strings.tooltips.currentLocation}
            >
              <FontAwesomeIcon icon={faLocationArrow} className="mr-2" />
              {strings.buttons.currentLocation}
            </button>

            {/* Use Location Button */}
            <button
              type="button"
              onClick={handleUseSelectedLocation}
              className="px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
              style={{
                backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                color: UI_COLORS.BUTTONS.PRIMARY.text,
              }}
            >
              {strings.buttons.useLocation}
            </button>

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                color: UI_COLORS.TEXT.MUTED,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <div className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>
              {error}
            </div>
          </div>
        )}

        {/* Map Container - No loading UI */}
        <div className="relative">
          <div
            ref={mapRef}
            style={{ height, width }}
            className="w-full"
          />
          
          {/* Instructions Overlay */}
          <div className="absolute bottom-4 left-4 right-4">
            <div
              className="bg-white rounded-lg p-3 shadow-lg border"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.CARD, 0.95),
                borderColor: UI_COLORS.BORDERS.LIGHT,
              }}
            >
              <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                <strong>{strings.instructions.split(':')[0]}:</strong> {strings.instructions.split(':')[1]}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Map;